import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { DropDownList, LookupDialogDimensions } from '@core/interfaces';
import { EventService, ListService, fromList } from '@core/services';
import { dialogAttributes } from '@util/dialog';

import { endpoints } from '@environment';

import { CarrierLookupRequest, CarrierLookupResponseBody } from '#types/carrier';

import { AuthService } from '@auth/services';
import { CarrierLookupDialogComponent } from '@carrier/carrier-lookup-dialog/carrier-lookup-dialog.component';
import { CONSTANTS } from '@carrier/constants';
import { CarrierActions } from '@carrier/state';

@Injectable({
  providedIn: 'root',
})
export class CarrierService {
  carrierLookupDialogDimensions$ = new BehaviorSubject<LookupDialogDimensions>({
    container: { width: 0, height: 0 },
    grid: { width: 0, height: 0 },
  });

  carrierList$ = this.store.select(fromList.selectCarrierList);

  carrierLookupSelectedCarrier$ = new BehaviorSubject<string>(null);
  carrierLookupSelectedCarrierMultiShip$ = new BehaviorSubject<string>(null);

  deliveryServiceCodeList$ = (carrier: string) => this.store.select(fromList.selectDeliveryServiceCodeList(carrier));

  carrierDropdownList$ = new BehaviorSubject<DropDownList>(null);

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(
    private authService: AuthService,
    private http: HttpClient,
    private listService: ListService,
    private store: Store,
    private translocoService: TranslocoService,
    private dialogService: DialogService,
    private eventService: EventService
  ) {
    this.translocoService
      .selectTranslation()
      .pipe(distinctUntilChanged())
      .subscribe(() => {
        this.authService.authenticated$.pipe(filter((authenticated) => authenticated)).subscribe(() => {
          this.initializeDropdownLists();
        });
      });
  }

  requestLists() {
    this.store.dispatch(CarrierActions.RequestLists());
  }

  requestDeliveryServiceCodeList(carrier: string) {
    if (carrier) {
      this.store.dispatch(CarrierActions.RequestDeliveryServiceCodeList({ carrier }));
    }
  }

  getCarrierLookup(payload: CarrierLookupRequest) {
    return this.http.post(endpoints.carrier.carrier, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.GRID_CARRIER_LOOKUP_DIALOG),
      map((response: CarrierLookupResponseBody) => response)
    );
  }

  initializeDropdownLists() {
    this.carrierList$.pipe(distinctUntilChanged()).subscribe((list) => {
      this.carrierDropdownList$.next(
        this.listService.getDropdownList(list, {
          key1: 'key',
          key2: 'value',
          key3: 'scac',
          key4: 'multiship',
          key5: 'carrier_type',
          header1: this.translate('core.services.list.dropdown.header.carrier'),
          header2: this.translate('core.services.list.dropdown.header.name'),
          header3: this.translate('core.services.list.dropdown.header.scac'),
          header4: this.translate('core.services.list.dropdown.header.multiship'),
          header5: this.translate('core.services.list.dropdown.header.carrier_type'),
        })
      );
    });
  }

  carrierLookupDialog(config: DynamicDialogConfig = {}): DynamicDialogRef {
    this.carrierLookupSelectedCarrier$.next(null);
    this.carrierLookupSelectedCarrierMultiShip$.next(null);

    return this.dialogService.open(CarrierLookupDialogComponent, {
      ...dialogAttributes,
      header: this.translate('carrier.carrier_lookup_dialog.dialog.title'),
      styleClass: 'lookup-dialog large-dialog',
      ...config,
    });
  }
}
